import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useWebSocketContext } from '@/context/WebSocketProvider';
import {
  clearAskHauserHistory,
  getAskHauserHistory,
  sendAskHauserMessage,
  type AskHauserMessage,
  type AskHauserMode,
  type AskHauserToolResult,
} from '@/services/api/ask-hauser/api';

export interface LocalChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  rawContent?: string;
  createdAt?: number;
  /** Structured tool results rendered as inline cards below the text.
   *  Populated either by replayed history or by ASK_HAUSER_TOOL_RESULT
   *  WebSocket events arriving during streaming. */
  toolResults?: AskHauserToolResult[];
  isStreaming?: boolean;
}

// Card questions send the visible question followed by hidden answering instructions (HOA due diligence, #1314).
// The history only stores the full prompt, so replayed messages show the question alone, as they did when sent.
const HIDDEN_INSTRUCTION_MARKERS = ['\n\nConduct buyer-side HOA due diligence'];

export const displayContentForAskHauserMessage = (message: AskHauserMessage): string => {
  if (message.role !== 'user') return message.content;
  for (const marker of HIDDEN_INSTRUCTION_MARKERS) {
    const at = message.content.indexOf(marker);
    if (at > 0) return message.content.slice(0, at).trim();
  }
  if (message.content.startsWith('Give me a structured property summary using exactly these four plain-text sections.')) {
    return 'Get the full property report';
  }
  if (message.content.startsWith('Give me a structured risk summary using exactly these four plain-text sections.')) {
    return 'Get the full risk analysis';
  }
  return message.content;
};

export function useAskHauser(
  propertyId: string | undefined,
  propertyContext?: Record<string, unknown>,
  mode?: AskHauserMode,
) {
  const queryClient = useQueryClient();
  const { lastAskHauserChunk, lastAskHauserComplete, lastAskHauserToolResult } =
    useWebSocketContext();

  const [localMessages, setLocalMessages] = useState<LocalChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const activeMessageIdRef = useRef<string | null>(null);
  const prevPropertyIdRef = useRef<string | undefined>(propertyId);
  // Chunk deduplication: track the last processed chunk index per stream
  const lastProcessedIndexRef = useRef<number>(-1);
  // Buffer chunks that arrive via WebSocket before the HTTP response sets activeMessageIdRef
  const chunkBufferRef = useRef<Array<{ messageId: string; chunk: string; index: number }>>([]);
  // Same buffer pattern for tool results — they can arrive between text
  // chunks and we need to render them inline below the text
  const toolResultBufferRef = useRef<
    Array<{ messageId: string; toolResult: AskHauserToolResult }>
  >([]);
  // Tool-result deduplication by index (mirrors chunk index — they share
  // the same monotonic counter on the backend so we just track the same way)
  const seenToolResultIndexesRef = useRef<Set<number>>(new Set());
  const awaitingMessageIdRef = useRef(false);

  // Reset local state when propertyId changes
  useEffect(() => {
    if (prevPropertyIdRef.current !== propertyId) {
      prevPropertyIdRef.current = propertyId;
      setLocalMessages([]);
      setIsTyping(false);
      activeMessageIdRef.current = null;
      lastProcessedIndexRef.current = -1;
      chunkBufferRef.current = [];
      toolResultBufferRef.current = [];
      seenToolResultIndexesRef.current = new Set();
      awaitingMessageIdRef.current = false;
    }
  }, [propertyId]);

  // Load history from backend
  const { data: historyData, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['askHauser', propertyId],
    queryFn: () => getAskHauserHistory(propertyId as string),
    enabled: !!propertyId,
    staleTime: 30_000,
  });

  // Sync history to local state when data arrives (after reset or first load)
  useEffect(() => {
    if (historyData?.messages && localMessages.length === 0) {
      const mapped: LocalChatMessage[] = historyData.messages.map((m: AskHauserMessage) => ({
        id: m.messageId,
        role: m.role,
        content: displayContentForAskHauserMessage(m),
        rawContent: m.content,
        createdAt: m.createdAt,
        toolResults: m.toolResults,
      }));
      setLocalMessages(mapped);
    }
  }, [historyData, localMessages.length]);

  // Append a chunk to the streaming assistant message (or create it)
  const appendChunk = useCallback((messageId: string, chunk: string) => {
    setLocalMessages((prev) => {
      const existing = prev.find((m) => m.id === messageId);
      if (existing) {
        return prev.map((m) =>
          m.id === messageId ? { ...m, content: m.content + chunk, isStreaming: true } : m,
        );
      }
      return [...prev, { id: messageId, role: 'assistant', content: chunk, isStreaming: true }];
    });
  }, []);

  // Append a tool result to the streaming assistant message (creating the
  // message if no text chunks have arrived yet).
  const appendToolResult = useCallback(
    (messageId: string, toolResult: AskHauserToolResult) => {
      setLocalMessages((prev) => {
        const existing = prev.find((m) => m.id === messageId);
        if (existing) {
          return prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  toolResults: [...(m.toolResults ?? []), toolResult],
                  isStreaming: true,
                }
              : m,
          );
        }
        return [
          ...prev,
          {
            id: messageId,
            role: 'assistant',
            content: '',
            toolResults: [toolResult],
            isStreaming: true,
          },
        ];
      });
    },
    [],
  );

  // Handle incoming WebSocket chunks
  useEffect(() => {
    if (!lastAskHauserChunk) return;
    const { messageId, chunk, index } = lastAskHauserChunk;

    // If HTTP response hasn't arrived yet, buffer the chunk for later
    if (awaitingMessageIdRef.current && !activeMessageIdRef.current) {
      chunkBufferRef.current.push({ messageId, chunk, index });
      return;
    }

    if (activeMessageIdRef.current !== messageId) return;

    // Deduplicate: skip chunks we've already processed
    if (index <= lastProcessedIndexRef.current) return;
    lastProcessedIndexRef.current = index;

    appendChunk(messageId, chunk);
  }, [lastAskHauserChunk, appendChunk]);

  // Handle incoming tool results (structured data cards rendered inline)
  useEffect(() => {
    if (!lastAskHauserToolResult) return;
    const { messageId, toolName, toolInput, result, index } = lastAskHauserToolResult;
    const toolResult: AskHauserToolResult = { toolName, toolInput, result, index };

    // Buffer if HTTP response hasn't returned the active message id yet.
    if (awaitingMessageIdRef.current && !activeMessageIdRef.current) {
      toolResultBufferRef.current.push({ messageId, toolResult });
      return;
    }

    if (activeMessageIdRef.current !== messageId) return;
    if (seenToolResultIndexesRef.current.has(index)) return;
    seenToolResultIndexesRef.current.add(index);

    appendToolResult(messageId, toolResult);
  }, [lastAskHauserToolResult, appendToolResult]);

  // Handle stream complete
  useEffect(() => {
    if (!lastAskHauserComplete) return;
    const { messageId } = lastAskHauserComplete;

    if (activeMessageIdRef.current !== messageId) return;

    setIsTyping(false);
    activeMessageIdRef.current = null;
    lastProcessedIndexRef.current = -1;
    chunkBufferRef.current = [];
    toolResultBufferRef.current = [];
    seenToolResultIndexesRef.current = new Set();
    awaitingMessageIdRef.current = false;

    setLocalMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, isStreaming: false } : m)),
    );

    queryClient.invalidateQueries({ queryKey: ['askHauser', propertyId] });
  }, [lastAskHauserComplete, propertyId, queryClient]);

  // Send message mutation
  const sendMutation = useMutation({
    mutationFn: ({ content }: { content: string; displayText?: string }) =>
      sendAskHauserMessage(propertyId as string, content, propertyContext, mode),
    onMutate: async ({ content, displayText }) => {
      const tempId = `user-${Date.now()}`;
      const now = Date.now();
      setLocalMessages((prev) => [...prev, {
        id: tempId,
        role: 'user',
        content: displayText ?? content,
        rawContent: content,
        createdAt: now,
      }]);
      setIsTyping(true);
      awaitingMessageIdRef.current = true;
      chunkBufferRef.current = [];
      toolResultBufferRef.current = [];
      seenToolResultIndexesRef.current = new Set();
      lastProcessedIndexRef.current = -1;
      return { tempId };
    },
    onSuccess: (data) => {
      activeMessageIdRef.current = data.messageId;
      awaitingMessageIdRef.current = false;

      // Flush any chunks that arrived before the HTTP response
      const buffered = chunkBufferRef.current
        .filter((c) => c.messageId === data.messageId)
        .sort((a, b) => a.index - b.index);
      chunkBufferRef.current = [];

      if (buffered.length > 0) {
        let combined = '';
        for (const c of buffered) {
          if (c.index <= lastProcessedIndexRef.current) continue;
          lastProcessedIndexRef.current = c.index;
          combined += c.chunk;
        }
        if (combined) {
          appendChunk(data.messageId, combined);
        }
      }

      // Same flush pattern for tool results buffered before the HTTP response.
      const bufferedTools = toolResultBufferRef.current
        .filter((t) => t.messageId === data.messageId)
        .sort((a, b) => a.toolResult.index - b.toolResult.index);
      toolResultBufferRef.current = [];
      for (const t of bufferedTools) {
        if (seenToolResultIndexesRef.current.has(t.toolResult.index)) continue;
        seenToolResultIndexesRef.current.add(t.toolResult.index);
        appendToolResult(data.messageId, t.toolResult);
      }
    },
    onError: () => {
      setIsTyping(false);
      activeMessageIdRef.current = null;
      lastProcessedIndexRef.current = -1;
      chunkBufferRef.current = [];
      toolResultBufferRef.current = [];
      seenToolResultIndexesRef.current = new Set();
      awaitingMessageIdRef.current = false;
    },
  });

  // Clear conversation mutation
  const clearMutation = useMutation({
    mutationFn: () => clearAskHauserHistory(propertyId as string),
    onSuccess: () => {
      setLocalMessages([]);
      queryClient.invalidateQueries({ queryKey: ['askHauser', propertyId] });
    },
  });

  const sendMessage = useCallback(
    (content: string, displayText?: string) => {
      if (!propertyId || !content.trim()) return;
      sendMutation.mutate({ content: content.trim(), displayText: displayText?.trim() });
    },
    [propertyId, sendMutation],
  );

  const clearConversation = useCallback(() => {
    if (!propertyId) return;
    clearMutation.mutate();
  }, [propertyId, clearMutation]);

  return {
    messages: localMessages,
    isTyping,
    isLoadingHistory,
    sendMessage,
    clearConversation,
    isSending: sendMutation.isPending,
  };
}
